import React from 'react';
import { Box, Button } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { encrypt } from '../../Encrypdecryp/Crypt.js';
import './ActionButtons.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL 

const ActionButtons = ({
  file,
  extractedData,
  averageMonthlyImportAmount,
  extractionComplete,
  setFile,
  setPdfUrl,
  setExtractedData,
  setAverageMonthlyImportAmount,
  setLoading,
  setError,
  setReferenceId,
  setPollingProgress,
  setExtractionComplete,
  setIsSaved,
  fetchEntities,
  pdfUrl,
  setEntities,
  validateSave,
  theme = 'dark',
}) => {
  const handleSave = async () => {
    // Validate averageMonthlyImportAmount
    if (!validateSave()) return;

    // Check for incomplete data
    if (!file || !extractedData || !file.documentId || !extractionComplete) {
      toast.warn('Please ensure a file is uploaded and data is extracted.', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === 'dark' ? '#fff3cd' : '#fef3c7',
          color: '#856404',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No authentication token found');
      const encryptedToken = encrypt(token);
      const finalizedData = {
        ...extractedData,
        // average_monthly_import_amount: averageMonthlyImportAmount ? parseFloat(averageMonthlyImportAmount) : null,
      };
      const requestBody = {
        finalized_data: finalizedData,
        token: encryptedToken,
      };
      const response = await axios.post(`${API_BASE_URL}/bank-account-opening/confirm/${file.documentId}`, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      console.log('Confirm response:', response.data);

      // Create new entity to prepend to entities
      const newEntity = {
        id: file.documentId || `temp-${Date.now()}`,
        applicant_name: String(extractedData.fullName || 'Unknown'),
        account_no: String(extractedData.titleOfAccount || '-'),
        branch: String(extractedData.branch || ''),
        riskRating: extractedData.riskRating || 'Low',
        documentId: file.documentId,
        initial_data: extractedData,
        finalized_data: finalizedData,
        extracted_data: extractedData,
        // average_monthly_import_amount: finalizedData.average_monthly_import_amount || null,
        created_at: new Date().toISOString(),
      };

      // Prepend new entity to entities
      setEntities((prev) => [newEntity, ...prev]);

      // Reset all states after successful save
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setFile(null);
      setPdfUrl(null);
      setExtractedData(null);
      if (setAverageMonthlyImportAmount) setAverageMonthlyImportAmount('');
      setLoading(false);
      setError(null);
      setReferenceId(null);
      setPollingProgress(0);
      setExtractionComplete(false);
      setIsSaved(true);

      toast.success('BankAccountOpening form saved successfully!', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === 'dark' ? '#d4edda' : '#d1fae5',
          color: '#155724',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });

      // Fetch entities to ensure consistency with backend
      await fetchEntities();
    } catch (error) {
      console.error('Confirm endpoint error:', error);
      toast.error('Failed to save the document: ' + (error.response?.data?.message || error.message), {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === 'dark' ? '#f8d7da' : '#fee2e2',
          color: '#721c24',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });
    }
  };

  const handleDelete = async () => {
    if (!file || !file.documentId) {
      toast.warn('Please upload a file to delete.', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === 'dark' ? '#fff3cd' : '#fef3c7',
          color: '#856404',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No authentication token found');
      const encryptedToken = encrypt(token);
      const requestBody = {
        token: encryptedToken,
      };
      const response = await axios.delete(`${API_BASE_URL}/bank-account-opening/${file.documentId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        data: requestBody,
      });

      if (response.status === 200) {
        // Reset all states after successful deletion
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setFile(null);
        setPdfUrl(null);
        setExtractedData(null);
        if (setAverageMonthlyImportAmount) setAverageMonthlyImportAmount('');
        setLoading(false);
        setError(null);
        setReferenceId(null);
        setPollingProgress(0);
        setExtractionComplete(false);
        setIsSaved(false);

        toast.success('Bank account opening form deleted successfully!', {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: theme === 'dark' ? '#d4edda' : '#d1fae5',
            color: '#155724',
            borderRadius: '8px',
            fontSize: '14px',
          },
        });

        // Fetch entities to update the list
        await fetchEntities();
      }
    } catch (error) {
      console.error('Delete endpoint error:', error);
      if (error.response && error.response.status === 422) {
        toast.error('Failed to delete the form: ' + (error.response.data.detail || 'Invalid reference ID or permission denied.'), {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: theme === 'dark' ? '#f8d7da' : '#fee2e2',
            color: '#721c24',
            borderRadius: '8px',
            fontSize: '14px',
          },
        });
      } else {
        toast.error('Failed to delete the document: ' + (error.response?.data?.message || error.message), {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: theme === 'dark' ? '#f8d7da' : '#fee2e2',
            color: '#721c24',
            borderRadius: '8px',
            fontSize: '14px',
          },
        });
      }
    }
  };

  return (
    <Box className={`action-buttons ${theme}-theme`}>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={!extractionComplete}
        className="newt-button newt-button-primary"
        // aria-label="Save form"
      >
        Save
      </Button>
      <Button
        variant="contained"
        onClick={handleDelete}
        className="newt-button newt-button-primary"
        // aria-label="Delete form"
      >
        Delete
      </Button>
    </Box>
  );
};

export default ActionButtons;